import React from 'react';
import { useNavigate } from 'react-router-dom';

const TermsAndConditionsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="terms-and-conditions-page">
      <div className="max-w-4xl mx-auto px-6 sm:px-12 lg:px-24 py-12 sm:py-16">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-4">Legal</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#0B1120] tracking-tight mb-4">Terms & Conditions</h1>
        <p className="text-slate-500 mb-12">Last Updated: April 8, 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using the website and services of Prestigious Travel Club Inc. ("we," "us," or "our") at
              prestigioustravelclub.org, you agree to be bound by these Terms and Conditions. If you do not agree to these terms,
              you may not use our website or services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">2. Description of Services</h2>
            <p>
              Prestigious Travel Club Inc. provides travel membership services, charitable programming through The Prestigious Travel
              Club Foundation, and related services. Our website provides information about our services, memberships, philanthropic
              initiatives, and allows users to submit inquiries, volunteer applications, and donations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">3. SMS / Text Messaging Consent</h2>
            <p>
              By providing your phone number and opting in through any form on our website, you consent to receive SMS text messages
              from Prestigious Travel Club Inc. regarding your inquiry, membership, services, and related communications.
            </p>
            <p className="mt-3 font-semibold">
              Message frequency may vary. Message and data rates may apply.
            </p>
            <p className="mt-3">
              SMS consent is not a condition of purchase or membership. You are not required to provide SMS consent to use our
              services or complete any transaction.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mt-4">
              <h3 className="font-semibold text-[#0B1120] mb-3">Opt-Out and Help Instructions</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>STOP:</strong> Reply <strong>STOP</strong> to any SMS message to immediately opt out of all text messages from Prestigious Travel Club Inc. You will receive a one-time confirmation message and no further texts will be sent.</li>
                <li><strong>HELP:</strong> Reply <strong>HELP</strong> to any SMS message for assistance, or contact us at Info@prestigioustravelclub.org or 407-796-1937.</li>
              </ul>
            </div>
            <p className="mt-4">
              You may also revoke your consent to receive SMS messages at any time by emailing Info@prestigioustravelclub.org,
              calling 407-796-1937, or writing to us at 1431 Simpson Road, #1067, Kissimmee, FL 34744.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">4. Communication Consent</h2>
            <p>
              By submitting your information through our website forms (contact forms, volunteer applications, membership inquiries,
              or donation forms), you consent to receive communications from Prestigious Travel Club Inc. via email, phone, and/or
              SMS as applicable. Communications may include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Responses to your inquiries and requests</li>
              <li>Service-related updates and notifications</li>
              <li>Membership information and account communications</li>
              <li>Volunteer coordination and event notifications</li>
              <li>Promotional materials and newsletters (with separate opt-in)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Use our website for any unlawful purpose or in violation of any applicable laws</li>
              <li>Submit false, misleading, or fraudulent information</li>
              <li>Attempt to gain unauthorized access to our systems or data</li>
              <li>Interfere with the proper functioning of our website</li>
              <li>Reproduce, distribute, or create derivative works from our content without permission</li>
              <li>Use automated systems (bots, scrapers) to access our website without authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">6. Intellectual Property</h2>
            <p>
              All content on this website, including but not limited to text, graphics, logos, images, and software, is the property
              of Prestigious Travel Club Inc. or its licensors and is protected by applicable intellectual property laws. You may not
              use, reproduce, or distribute any content without our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">7. Donations and Charitable Contributions</h2>
            <p>
              Donations made through our website are processed by Givebutter, a third-party donation platform. All donations to
              The Prestigious Travel Club Foundation are tax-deductible as permitted by law. Refund policies for donations are
              subject to applicable law and our internal refund policy. Please contact us for any donation-related inquiries.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">8. Service Limitations</h2>
            <p>
              We strive to provide accurate and up-to-date information on our website; however, we do not warrant that the content
              is error-free, complete, or current. Travel availability, pricing, and membership benefits are subject to change without
              notice. We reserve the right to modify, suspend, or discontinue any part of our services at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">9. Disclaimer of Warranties</h2>
            <p>
              Our website and services are provided "as is" and "as available" without warranties of any kind, either express or
              implied, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, and
              non-infringement. We do not guarantee that our website will be uninterrupted, secure, or error-free.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">10. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Prestigious Travel Club Inc., its officers, directors, employees, and agents
              shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited
              to loss of profits, data, or other intangible losses, arising out of or relating to your use of our website or services.
              Our total liability for any claims arising under these terms shall not exceed the amount you paid to us, if any, in the
              twelve (12) months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Prestigious Travel Club Inc. and its affiliates from any claims,
              liabilities, damages, losses, and expenses arising out of your use of our website or services, your violation of these
              terms, or your violation of any rights of a third party.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">12. Governing Law and Jurisdiction</h2>
            <p>
              These Terms and Conditions shall be governed by and construed in accordance with the laws of the State of Florida,
              without regard to its conflict of law provisions. Any disputes arising under or in connection with these terms shall
              be subject to the exclusive jurisdiction of the state and federal courts located in Osceola County, Florida.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">13. Changes to These Terms</h2>
            <p>
              We reserve the right to update or modify these Terms and Conditions at any time. Changes will be posted on this page
              with an updated "Last Updated" date. Your continued use of our website after any modifications constitutes your
              acceptance of the revised terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">14. Support Contact</h2>
            <p>
              For questions, concerns, or requests related to these Terms and Conditions, please contact us:
            </p>
            <p className="mt-3">
              <strong>Prestigious Travel Club Inc.</strong><br />
              1431 Simpson Road, #1067<br />
              Kissimmee, Florida 34744<br />
              Phone: 407-796-1937<br />
              Email: Info@prestigioustravelclub.org
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
